import {ChangeDetectionStrategy, Component, EventEmitter, Input, NgZone, OnInit, Output} from '@angular/core';
import {OrderByPipe} from 'angular-pipes';

@Component({
  selector: 'gen-sample-list',
  templateUrl: './sample-list.component.html',
  styleUrls: ['./sample-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [OrderByPipe]
})
export class SampleListComponent implements OnInit {

  @Output() updateSamples: EventEmitter<Array<any>> = new EventEmitter<Array<any>>();
  @Input() callback: () => void;
  @Input() primaryid: string;
  @Input() allowedit: boolean;
  @Input() disabledForced: boolean;
  @Input() editMode: boolean;
  @Input() markAsPrimaryEnabled: boolean;
  @Input() proceedToAnaliseStarted: boolean;
  @Input() overrides: any;
  @Input() specimensConfig: Array<any> = [];
  @Input() set samples(samples: Array<any>) {
   this.setSamples(samples);
  }

  private setSamples(samples: Array<any>) {
    let newSamples;

    if (this.specimensConfig) {
      for (let i = 0; i < samples.length; i++) {
        samples[i].config = this.getSpecimenConfigForSample(samples[i]);
      }
    }

    newSamples = samples.sort((a, b) => {
      for (const fn of this.sortBy) {
        const sortRes = fn.apply(this, [a, b]);

        if (sortRes !== 0) {
          return sortRes;
        }
      }

      return 0;
    });

    const newSamplesDiff = this.getDiffValue(newSamples);
    const currentSampleDiff = this.getDiffValue(this._samples);

    if (newSamplesDiff !== currentSampleDiff) {
      this._samples = newSamples;
    }
  }

  get samples(): Array<any> {
    return this._samples;
  }

  constructor(
      private OrderByPipe: OrderByPipe,
      private _zone: NgZone
  ) {
  }

  private _samples: Array<any> = [];
  private sortBy: Array<(a: Sample, b: Sample) => number> = [this.sortByPrimaryId, this.sortByTumorType, this.sortByCreationDate];

  samplesCompare(index, sample) {
    return `${sample.id}_${sample.isPrimary}`;
  }

  private getSpecimenConfigForSample(sample): any {
    return this.specimensConfig.find(conf => {
      const configSampleType = !!conf.sampleType ? conf.sampleType : null;
      const specimenSampleType = !!sample.sampleType ? sample.sampleType : null;
      const configObjectType = !!conf.specimenType ? conf.specimenType : null;
      const specimenObjectType = !!sample.objectType ? sample.objectType : null;

      return configSampleType === specimenSampleType && configObjectType === specimenObjectType;
    });
  }

  onAccordionOpen(event: any): void {
    const accordionTabElement: HTMLElement = event.originalEvent.target.closest('p-accordiontab');
    this._zone.runOutsideAngular(() => {
      setTimeout(() => {
        accordionTabElement.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    });
  }

  ngOnInit() {
    this.setSamples(this.samples);
  }

  public updateSample(originalSample, update) {
    Object.assign(originalSample, update);
    this.updateSamples.emit(this.samples);
  }

  public primarySampleChanged(updatedSamples) {
    // Updates the changed samples only
    this.samples.forEach(sample => {
      const updatedSample = updatedSamples.find(s => s.id === sample.id);

      if (updatedSample) {
        Object.assign(sample, updatedSample);
      }
    });

    // The cloning is for making this change to be passed to the sample inputs which contained by the sample list (for ex: dynamic-specimen)
    this.samples = JSON.parse(JSON.stringify(this.samples));
    this.updateSamples.emit(this.samples);
  }

  private getDiffValue(samples: Array<Sample>): string {
    let sampleIds = '';
    samples.forEach(s => sampleIds += s.id + '_');

    return sampleIds;
  }

  private sortByPrimaryId(a: Sample, b: Sample): number {
    if (a.id === this.primaryid) {
      return -1;
    } else if (b.id === this.primaryid) {
      return 1;
    } else {
      return 0;
    }
  }

  private sortByTumorType(a: Sample, b: Sample): number {
    if (!(a.config && b.config && a.config.specimenType && b.config.specimenType)) {
      return 0;
    }

    const aType = (!!a.sampleType) === true ? a.sampleType : a.config.specimenType;
    const bType = (!!b.sampleType) === true ? b.sampleType : b.config.specimenType;

    if (aType === bType) {
      return 0;
    }

    if (bType === SpecimenType.TUMOR) {
      return 1;
    }

    return -1;
  }

  private sortByCreationDate(a: Sample, b: Sample): number {
    if (!(a.meta && b.meta && a.meta.created && b.meta.created)) {
      return 0;
    }

    const aCreated = new Date(a.meta.created);
    const bCreated = new Date(b.meta.created);

    if (aCreated > bCreated) {
      return 1;
    } else if (aCreated < bCreated) {
      return -1;
    } else {
      return 0;
    }
  }

  public buildAutomationClass(sample) {
    if (!sample) {
      return '';
    }

    let automationSampleClass = '';
    const type = sample.sampleType || sample.objectType;

    if (!type) {
      return '';
    }

    if (sample.config && sample.config.formId) {
      const configString = sample.config.formId;
      automationSampleClass = `Auto_${type}_${configString}`;
    } else {
      automationSampleClass = `Auto_${type}`;
    }

    return automationSampleClass;
  }
}

interface Sample {
  id: string;
  meta?: {
    created: string;
    lastUpdated?: string;
    versionId?: string;
  };
  config?: {
    specimenType: string
  };
  sampleType?: string;

}

const SpecimenType = {
  DYNAMIC: 'dynamic',
  NAVICAN: 'navican',
  NORMAL: 'normal',
  TUMOR: 'tumor'
};
